import * as React from "react";
import {observer} from "mobx-react";
import {withAppStores} from "../../../../AppStores";
import type {TransportLayer} from "../../../../data/TransportLayer";
import {FieldDataType, Permission} from "../../../../generated/api/base";
import type {AppState} from "../../../../data/state/AppState";
import {featureTitles} from "../../../../data/state/AppStateConstants";
import {Report} from "../../../../data/models/Report";
import {TimeUtils} from "../../../../utils/TimeUtils";
import {FieldV5} from "../../details/FieldV5";
import {FieldInputV5} from "../../details/datatypes/FieldInputV5";
import {StringUtils} from "../../../../utils/data/string/StringUtils";

interface IReportDefaultFieldsProps {
	readonly item: Report;
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
}

interface IReportDefaultFieldsState {
	updatingName: boolean;
	updatingDescription: boolean;
}

@withAppStores("appState", "transport")
@observer
export class ReportDefaultFieldsV5 extends React.Component<IReportDefaultFieldsProps, IReportDefaultFieldsState> {
	constructor(props: IReportDefaultFieldsProps) {
		super(props);
		this.state = {
			updatingName: false,
			updatingDescription: false,
		};
	}

	private onNameChange = async (value: string) => {
		const {item, transport} = this.props;

		if (item.name === value.trim()) {
			return;
		}

		item.name = value;

		this.setState({updatingName: true});
		await TimeUtils.waitUpdate(transport.appState.actions.updateReport(item), this.props.appState.app.notificationContainer);
		this.setState({updatingName: false});
	};

	private onDescriptionChange = async (value: string) => {
		const {item, transport} = this.props;

		item.description = value;

		this.setState({updatingDescription: true});
		await TimeUtils.waitUpdate(transport.appState.actions.updateReport(item), this.props.appState.app.notificationContainer);
		this.setState({updatingDescription: false});
	};

	private isReportReadonly = (report: Report) => {
		const permission = Report.getPermission(report, this.props.appState);

		if (permission < Permission.Update) {
			return true;
		}

		return false;
	};

	private getErrorMessage = (input: string) => {
		const inputClean = StringUtils.removeWhiteSpaces(input);

		if (!inputClean) {
			return "Name cannot be empty!";
		}
	};

	public override render() {
		const {item, appState} = this.props;
		const {updatingName, updatingDescription} = this.state;
		const isReadonly = this.isReportReadonly(item);

		return (
			<>
				<FieldV5 label={appState.actions.getRefIdName(item.ownFeature)}>{item.refId}</FieldV5>
				<FieldV5
					label="Name"
					className="stucked"
				>
					{/* TODO: throughout the app, we should update the "readonly" styling (grayed out text) as defined here https://dev.azure.com/xyicon/SpaceRunner%20V4/_workitems/edit/7101/ */}
					{isReadonly ? (
						item.name
					) : (
						<FieldInputV5
							dataType={FieldDataType.SingleLineText}
							dataTypeSettings={{}}
							value={item.name}
							onChange={this.onNameChange}
							isUpdating={updatingName}
							getErrorMessage={this.getErrorMessage}
						/>
					)}
				</FieldV5>
				<FieldV5 label="Category">{item.category}</FieldV5>
				<FieldV5 label="Module">{featureTitles[item.reportFeature]}</FieldV5>
				<FieldV5 label="Description">
					{isReadonly ? (
						item.description
					) : (
						<FieldInputV5
							dataType={FieldDataType.MultiLineText}
							dataTypeSettings={{}}
							value={item.description}
							onChange={this.onDescriptionChange}
							isUpdating={updatingDescription}
						/>
					)}
				</FieldV5>
			</>
		);
	}
}
